import React from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { fromGlobalId } from 'graphql-relay';

import media from 'lib/media/media';
import ImageView from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import like from 'four-nets/icons/svg/icon_like-2.svg';
import comment from 'four-nets/icons/svg/icon_comment.svg';

import { ImageEdgesFragment } from '../../album-query.graphql';
import { calculateImageGrid, GridItem } from './utils';
import defaultStyles from './nice-image-grid.scss';

export interface Props {
    styles: typeof defaultStyles;
    images: ImageEdgesFragment;
    imgRef?: ((instance: HTMLImageElement | null, props: Props) => void) | React.Ref<HTMLImageElement>;
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, props: any) => void;
    wrapWidth?: number;
    imageIdToHref?: (imageId: string) => string;
    showStatsOnHover?: boolean;
}

interface State {
    lg?: boolean;
}

class NiceImageGrid extends React.PureComponent<Props> {
    static defaultProps = {
        wrapWidth: 1110,
    };

    state: State = {};
    list?: MediaQueryList;
    wrapperRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this.list = window.matchMedia(media.lg);
        this.list.addListener(this.handleResize);
        this.handleResize(this.list);
    }

    componentWillUnmount() {
        if (this.list) {
            this.list.removeListener(this.handleResize);
        }
    }

    handleResize = (event: any) => {
        this.setState({ lg: event.matches });
    };

    onClick = (index: number, gridItem: GridItem) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();

        if (this.props.onClick) {
            this.props.onClick(event, { index, ...this.props, ...gridItem });
        }
    };

    onRef = (ref: HTMLImageElement | null) => {
        if (this.props.imgRef instanceof Function) {
            this.props.imgRef(ref, this.props);
        }
    };

    render() {
        const { styles, images, imageIdToHref, showStatsOnHover } = this.props;
        const { lg } = this.state;
        const wrapWidth = lg
            ? this.props.wrapWidth!
            : this.wrapperRef.current
            ? this.wrapperRef.current.clientWidth
            : 500;

        let elementType = 'div';
        if (imageIdToHref) {
            elementType = 'a';
        }

        return (
            <div
                ref={this.wrapperRef}
                className={classnames(styles.grid, {
                    [styles.showStatsOnHover]: showStatsOnHover,
                })}
            >
                {calculateImageGrid(images, wrapWidth).map((row, index) => (
                    <div className={styles.row} key={`row-${index}`}>
                        {row.map(gridItem => {
                            const {
                                width,
                                height,
                                commentCount,
                                likesCount,
                                inViewerCollections,
                                isCreatedByViewer,
                                isSubscribedByViewer,
                                isLikedByViewer,
                                createdTime,
                                photoFile,
                                ...rest
                            } = gridItem;

                            const pk = fromGlobalId(gridItem.id).id;

                            return React.createElement(
                                elementType,
                                {
                                    key: `row-${index}-col-${gridItem.index}`,
                                    onClick: this.onClick(gridItem.index, gridItem),
                                    className: styles.image,
                                    style: lg
                                        ? { ...gridItem, position: 'relative' } // FIXME: find why server render sometimes ommits 'class' prop
                                        : {
                                              ...gridItem,
                                              position: 'relative',
                                              height: height,
                                              width: `${(width / wrapWidth) * 100}%`,
                                          },
                                    href: imageIdToHref ? imageIdToHref(pk) : undefined,
                                },
                                [
                                    <ImageView
                                        key={0}
                                        aspectFill={true}
                                        sizes={{ xs: wrapWidth / row.length }}
                                        linkify={false}
                                        {...rest}
                                    />,
                                    <div key={1} className={styles.stats}>
                                        <div className={styles.statsRow}>
                                            {likesCount > 0 ? (
                                                <div className={styles.stat}>
                                                    <SVGIcon style={{ image: styles.statIcon }} src={like} />
                                                    {likesCount}
                                                </div>
                                            ) : null}
                                            {commentCount > 0 ? (
                                                <div className={styles.stat}>
                                                    <SVGIcon style={{ image: styles.statIcon }} src={comment} />
                                                    {commentCount}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>,
                                ]
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    }
}

export default Styled(defaultStyles)(NiceImageGrid);
